import React from "react";
import { Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getThemeAwareBackgroundColor } from "./Utility";
import { useTheme } from "../../../context/ThemeContext";
import moment from "moment";
import "./Style.css";

export interface CalendarEvent {
  Id: string | number;
  StartTime?: string | Date;
  EndTime?: string | Date;
  DurationInMinutes?: number | string;
  Title?: string;
  Location?: string;
  MeetingLink?: string;
  Color?: string;
}

interface EventCardProps {
  event: CalendarEvent;
  onEventCardClick?: (event: CalendarEvent) => void;
  onJoinMeeting: (event: CalendarEvent) => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onJoinMeeting,
  onEventCardClick,
}) => {
  const { isDarkMode } = useTheme();

  return (
    <div
      key={event.Id}
      className="eventCardStyle"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onEventCardClick && onEventCardClick(event);
      }}
      style={{
        borderLeftColor: event.Color || "hsl(var(--border))",
        background: event.Color
          ? getThemeAwareBackgroundColor(event.Color, "#f5f5f5", isDarkMode)
          : "hsl(var(--muted))",
      }}
    >
      <div className="flex items-center h-full">
        {/* LEFT SECTION - Time + Duration */}
        <div className="w-[60px] flex flex-col items-start justify-center gap-px">
          <span className="event-time">{`${moment.utc(event?.StartTime).local().format("h:mm A")}`}</span>
          <span className="event-duration">{event.DurationInMinutes}</span>
        </div>

        {/* CENTER SECTION */}
        <div className="flex-1 min-w-0 px-2 flex flex-col gap-px">
          <span className="event-title" title={event.Title}>
            {event.Title}
          </span>
          <span className="event-location" title={event.Location}>
            {event.Location}
          </span>
        </div>

        {/* RIGHT SECTION - Join Icon */}
        <div className="w-8 flex items-center justify-center">
          {event.MeetingLink && (
            <Button
              variant="ghost"
              size="icon"
              className="join-meeting-btn h-8 w-8 p-0"
              title="Join meeting"
              aria-label="Join meeting"
              onClick={(e) => {
                e.stopPropagation();
                onJoinMeeting(event);
              }}
            >
              <Video className="h-[18px] w-[18px] text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
